/**
 * Wraps a client so that chosen mutating methods invalidate attention tags
 * once they succeed. A rejected call rethrows unchanged and invalidates
 * nothing; unlisted members pass straight through with `this` preserved.
 */
import { invalidateAttention } from './attentionStore';

export function withAttentionInvalidation<C extends object>(
  client: C,
  tags: Partial<Record<keyof C, string[]>>,
): C {
  return new Proxy(client, {
    get(target, prop, receiver) {
      const value: unknown = Reflect.get(target, prop, receiver);
      const methodTags = Object.hasOwn(tags, prop)
        ? (tags as Record<PropertyKey, string[] | undefined>)[prop]
        : undefined;
      if (typeof value !== 'function') return value;
      if (!methodTags) return (...args: unknown[]) => value.apply(target, args);
      return async (...args: unknown[]) => {
        const result: unknown = await value.apply(target, args);
        for (const tag of methodTags) invalidateAttention(tag);
        return result;
      };
    },
  });
}
